import { DEFAULT_TOPOLOGY_REF_TOLERANCE } from '../../topology/TopologyReferenceBuilders';
type RawPoint3D = [number, number, number];

/**
 * Puppy CAD 内核 边快照引用点。
 *
 * 设计说明：
 * 1. 普通边使用真实 Puppy CAD 内核 顶点作为引用点，可继续暴露为选择点；
 * 2. 闭合圆/椭圆边可能没有显式拓扑顶点，只能使用采样点建立稳定边引用；
 * 3. 采样点只服务边界线识别，不应向上游伪装成真实顶点。
 */
export interface TopologyEdgeReferencePoint {
  /** 参与边稳定引用计算的三维点。 */
  point: RawPoint3D;
  /** 该点在拓扑快照中的顶点角色。 */
  role: TopologySnapshotVertexRole;
}

/**
 * 判断两点是否可作为同一闭合曲边的不同参考点。
 *
 * @param left 左侧点。
 * @param right 右侧点。
 * @returns 是否不同。
 */
function areDistinctTopologyPoints(left: RawPoint3D, right: RawPoint3D): boolean {
  return Math.hypot(
    left[0] - right[0],
    left[1] - right[1],
    left[2] - right[2],
  ) > DEFAULT_TOPOLOGY_REF_TOLERANCE;
}

/**
 * 为 Puppy CAD 内核 边解析快照参考点。
 *
 * 设计说明：
 * 1. 整圆/整椭圆边在 Puppy CAD 内核 中可能没有两个显式拓扑顶点；
 * 2. 但它们仍然是端面真实边界，必须进入拓扑快照供轮廓线显示和后续选择使用；
 * 3. 当显式顶点不足时，使用曲线采样点中的首个点与一个不同点作为稳定参考点。
 *
 * @param edgeVertices Puppy CAD 内核 顶点解析结果。
 * @param sampledPoints 曲线采样点。
 * @returns 两个参考点；无法解析时返回 `null`。
 */
export function resolveEdgeReferencePoints(
  edgeVertices: RawPoint3D[],
  sampledPoints: TopologyPoint3D[] | undefined,
): [TopologyEdgeReferencePoint, TopologyEdgeReferencePoint] | null {
  if (edgeVertices.length >= 2 && areDistinctTopologyPoints(edgeVertices[0]!, edgeVertices[1]!)) {
    return [
      { point: edgeVertices[0]!, role: 'topological' },
      { point: edgeVertices[1]!, role: 'topological' },
    ];
  }
  if (!sampledPoints || sampledPoints.length < 2) {
    return null;
  }
  const firstPoint = sampledPoints[0] as RawPoint3D;
  const distinctPoint = sampledPoints.find((point) => (
    areDistinctTopologyPoints(firstPoint, point as RawPoint3D)
  )) as RawPoint3D | undefined;
  return distinctPoint
    ? [
      { point: firstPoint, role: 'curve-reference' },
      { point: distinctPoint, role: 'curve-reference' },
    ]
    : null;
}
